/**
 * Kits: the colours a sprite is allowed to use, by role.
 *
 * A spec never names a colour. It says `casing` or `trim_lit`, and the kit
 * decides what that is. Two things follow. Swapping the kit reskins everything
 * at once, and the palette cannot quietly fork, which it already had in the
 * hand-written generators: five values for the casing colour across the same
 * set of machines, chosen by nobody.
 *
 * **Kits are data, not code.** They are JSON files in `kits/`. The CLI loads them
 * from disk (see node-kits.ts) and the web build bundles them, which is why
 * this module touches no filesystem: it has to run in a browser too. Nothing here needs
 * editing to work in a style this repo has never seen: write a JSON file with
 * your own roles and point a spec at it. Glyphs load the same way, so a kit can
 * bring its own marks.
 *
 * What stays code is the shape library, because that is drawing logic rather
 * than configuration. That is the boundary: the craft is code, everything that
 * describes a sprite is data.
 */

import { SpecError } from "./error.js";
import type { Rgba } from "./raster.js";

export interface Kit {
	name: string;
	roles: Record<string, Rgba>;
	/** Named colour runs for value-driven parts, ordered low to high. */
	ramps: Record<string, string[]>;
	/** Marks this kit adds on top of the shared set. */
	glyphs?: Record<string, string[]>;
}

/**
 * A kit as somebody writes it, before parsing.
 *
 * Only the palette is compulsory. Most kits define no ramps and no marks of
 * their own, and requiring empty objects for both would make the smallest
 * useful kit two lines longer for no reason. `parseKit` fills them in, so
 * everything downstream can rely on a whole `Kit`.
 */
export interface KitSource {
	name: string;
	roles: Record<string, Rgba>;
	ramps?: Record<string, string[]>;
	glyphs?: Record<string, string[]>;
}

export const KITS: Record<string, Kit> = {};

/**
 * Glyphs live here rather than in glyphs.ts because a kit may bring its own,
 * and loading a kit must be able to register them. glyphs.ts re-exports these
 * so the validator can reach them without importing the shape library.
 */
export const GLYPHS: Record<string, string[]> = {};
export const KNOWN_GLYPHS = new Set<string>();

export function addGlyphs(source: Record<string, string[]>, from: string): void {
	for (const [name, rows] of Object.entries(source)) {
		if (!Array.isArray(rows) || rows.some((r) => typeof r !== "string")) {
			throw new SpecError(`${from}: glyph "${name}" must be a list of strings`);
		}
		if (new Set(rows.map((r) => r.length)).size > 1) {
			throw new SpecError(`${from}: glyph "${name}" has rows of different lengths`);
		}
		GLYPHS[name] = rows;
		KNOWN_GLYPHS.add(name);
	}
}

function isRgba(v: unknown): v is Rgba {
	return Array.isArray(v) && v.length === 4 && v.every((n) => typeof n === "number" && n >= 0 && n <= 255);
}

/**
 * Roles every kit has, whether it declares them or not.
 *
 * MEASURED. On a bench run of thirty draws, the single commonest way a draw
 * failed outright was a part naming the role `white` in a kit that never
 * defined one, and the repair round did not save it: told the exact problem and
 * given the list of legal roles, the model asked for `white` again. That is not
 * a model being careless. Pixel art wants a specular highlight on nearly
 * everything, so the concept is needed on every sprite, and a format that makes
 * you remember to declare it will be forgotten on the sprites that need it
 * most.
 *
 * So the fix is to make the request legal rather than to keep asking for it to
 * stop. These four are the ones with only one sensible value: white is white,
 * black is black, nothing is nothing, and an outline is the near-black the site
 * draws every sprite against.
 *
 * A kit that declares any of them wins. That matters for `outline` especially,
 * where a kit with its own ink should keep it, and it means no existing sprite
 * renders differently than it did before.
 */
export const UNIVERSAL_ROLES: Record<string, Rgba> = {
	none: [0, 0, 0, 0],
	white: [255, 255, 255, 255],
	black: [0, 0, 0, 255],
	outline: [26, 25, 33, 255],
};

/**
 * Rejects a malformed kit at load time rather than at draw time. A kit with a
 * typo'd colour would otherwise surface as one wrong pixel in one sprite,
 * which is exactly the class of bug this whole tool exists to stop.
 */
export function parseKit(raw: unknown, source: string): Kit {
	const k = raw as Partial<Kit>;
	if (!k || typeof k !== "object") throw new SpecError(`${source}: not an object`);
	if (typeof k.name !== "string" || !k.name) throw new SpecError(`${source}: needs a "name"`);
	if (!k.roles || typeof k.roles !== "object") throw new SpecError(`${source}: needs a "roles" object`);
	for (const [role, value] of Object.entries(k.roles)) {
		if (!isRgba(value)) {
			throw new SpecError(`${source}: role "${role}" must be [r, g, b, a] with each 0..255, got ${JSON.stringify(value)}`);
		}
	}
	for (const [ramp, steps] of Object.entries(k.ramps ?? {})) {
		if (!Array.isArray(steps) || steps.some((s) => typeof s !== "string")) {
			throw new SpecError(`${source}: ramp "${ramp}" must be a list of role names`);
		}
		for (const step of steps) {
			// Universal roles count as defined, or a ramp could not reach for white.
			if (!k.roles[step] && !UNIVERSAL_ROLES[step])
				throw new SpecError(`${source}: ramp "${ramp}" names role "${step}", which this kit does not define`);
		}
	}
	// The kit's own roles last, so anything it declares beats the universal one.
	return {
		name: k.name,
		roles: { ...UNIVERSAL_ROLES, ...(k.roles as Record<string, Rgba>) },
		ramps: k.ramps ?? {},
		glyphs: k.glyphs,
	};
}

export function registerKit(kit: Kit): void {
	KITS[kit.name] = kit;
	if (kit.glyphs) addGlyphs(kit.glyphs, `kit ${kit.name}`);
}

export function resolveKit(name: string): Kit {
	const kit = KITS[name];
	if (!kit) {
		throw new SpecError(
			`unknown kit "${name}". Known kits: ${Object.keys(KITS).sort().join(", ")}. ` +
				`A spec can also carry its own kit inline instead of naming one.`,
		);
	}
	return kit;
}

/**
 * A spec's `kit`: either the name of a registered kit, or a whole kit written
 * out in place.
 *
 * The named form is the one that matters for a project, because the point of a
 * kit is that many sprites share it. But a one-off sprite has nobody to share
 * with, and making it register a kit first would mean the format could only
 * describe colours somebody had already decided on. Inline kits mean a sprite
 * is self-contained: the file says what its colours are, and it renders
 * anywhere without setup.
 */
export function kitFrom(ref: string | KitSource, source = "spec.kit"): Kit {
	if (typeof ref === "string") return resolveKit(ref);
	const kit = parseKit(ref, source);
	// An inline kit may bring its own marks, and the validator looks glyphs up in
	// the shared set, so they have to be registered before a part can name one.
	if (kit.glyphs) addGlyphs(kit.glyphs, source);
	return kit;
}

export function resolveRole(kit: Kit, role: string): Rgba {
	const c = kit.roles[role];
	if (!c) {
		throw new SpecError(
			`unknown colour role "${role}" in kit "${kit.name}". ` +
				`Known roles: ${Object.keys(kit.roles).sort().join(", ")}`,
		);
	}
	return c;
}

/** A ramp resolved to actual colours, low to high. */
export function resolveRamp(kit: Kit, name: string): Rgba[] {
	const ramp = kit.ramps[name];
	if (!ramp) {
		throw new SpecError(
			`unknown ramp "${name}" in kit "${kit.name}". ` + `Known ramps: ${Object.keys(kit.ramps).sort().join(", ")}`,
		);
	}
	return ramp.map((role) => resolveRole(kit, role));
}

/**
 * When a role earns its place, and when it is just a hex value wearing a name.
 *
 * A kit is a shared palette. The reason specs name roles instead of colours is
 * that one table decides what "casing" is, so it cannot fork. Adding a role
 * that exactly one sprite uses gives that back: the same hex, moved, with an
 * extra step.
 *
 * So a role wants the same test as a shape: more than one sprite would reach
 * for it. `readout_off`/`on`/`hot` pass, because the apothecary and the med
 * synthesizer arrived at identical values independently. The `vial_*` set does
 * not yet, and is kept only because folding it into existing roles would push
 * that sprite from a recolour into a visible redraw.
 */
